/**
 * Mordeczka.eu Admin - Seasons Manager Component
 *
 * Component for managing series seasons (add/edit/delete)
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  InputNumber,
  Space,
  message,
  Popconfirm,
  Typography,
  Empty,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  seasonsApi,
  type SeasonCreate,
  type SeasonResponse,
} from "../services/api";

const { Title } = Typography;

interface SeasonsManagerProps {
  workId: string;
  seasons: SeasonResponse[];
  readOnly?: boolean;
}

export function SeasonsManager({
  workId,
  seasons,
  readOnly = false,
}: SeasonsManagerProps) {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSeason, setEditingSeason] = useState<SeasonResponse | null>(
    null,
  );
  const [form] = Form.useForm();

  // Create season mutation
  const createMutation = useMutation({
    mutationFn: (data: SeasonCreate) => seasonsApi.create(workId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work", workId] });
      message.success("Sezon dodany pomyślnie");
      handleCloseModal();
    },
    onError: (error: any) => {
      const detail = error.response?.data?.detail;
      message.error(detail || "Nie udało się dodać sezonu");
    },
  });

  // Update season mutation
  const updateMutation = useMutation({
    mutationFn: ({
      seasonId,
      data,
    }: {
      seasonId: string;
      data: SeasonCreate;
    }) => seasonsApi.update(workId, seasonId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work", workId] });
      message.success("Sezon zaktualizowany pomyślnie");
      handleCloseModal();
    },
    onError: () => message.error("Nie udało się zaktualizować sezonu"),
  });

  // Delete season mutation
  const deleteMutation = useMutation({
    mutationFn: (seasonId: string) => seasonsApi.delete(workId, seasonId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work", workId] });
      message.success("Sezon usunięty");
    },
    onError: () => message.error("Nie udało się usunąć sezonu"),
  });

  const handleOpenModal = (season?: SeasonResponse) => {
    if (readOnly) return;
    if (season) {
      setEditingSeason(season);
      form.setFieldsValue({
        season_number: season.season_number,
        year: season.year,
        episodes_count: season.episodes_count,
        episode_duration_minutes: season.episode_duration_minutes,
        total_duration_minutes: season.total_duration_minutes,
      });
    } else {
      setEditingSeason(null);
      // Suggest next season number
      const nextNumber =
        seasons.length > 0
          ? Math.max(...seasons.map((s) => s.season_number)) + 1
          : 1;
      form.setFieldsValue({ season_number: nextNumber });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSeason(null);
    form.resetFields();
  };

  const handleSubmit = (values: SeasonCreate) => {
    if (editingSeason) {
      updateMutation.mutate({ seasonId: editingSeason.id, data: values });
    } else {
      createMutation.mutate(values);
    }
  };

  // Calculate total duration from episodes if not provided
  const handleEpisodesChange = () => {
    const episodes = form.getFieldValue("episodes_count");
    const duration = form.getFieldValue("episode_duration_minutes");
    if (episodes && duration) {
      form.setFieldsValue({ total_duration_minutes: episodes * duration });
    }
  };

  const columns: any[] = [
    {
      title: "Sezon",
      dataIndex: "season_number",
      key: "season_number",
      width: 80,
      render: (num: number) => (
        <strong>S{num.toString().padStart(2, "0")}</strong>
      ),
    },
    {
      title: "Rok",
      dataIndex: "year",
      key: "year",
      width: 100,
      render: (year: number | null) => year || "-",
    },
    {
      title: "Odcinki",
      dataIndex: "episodes_count",
      key: "episodes_count",
      width: 100,
      render: (count: number | null) => count || "-",
    },
    {
      title: "Czas odcinka (min)",
      dataIndex: "episode_duration_minutes",
      key: "episode_duration_minutes",
      width: 140,
      render: (duration: number | null) => (duration ? `${duration} min` : "-"),
    },
    {
      title: "Łączny czas",
      dataIndex: "total_duration_minutes",
      key: "total_duration_minutes",
      width: 120,
      render: (duration: number | null) => {
        if (!duration) return "-";
        const hours = Math.floor(duration / 60);
        const mins = duration % 60;
        return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
      },
    },
  ];

  if (!readOnly) {
    columns.push({
      title: "Akcje",
      key: "actions",
      width: 120,
      render: (_: unknown, record: SeasonResponse) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
            size="small"
          />
          <Popconfirm
            title="Usuń sezon"
            description={`Czy na pewno chcesz usunąć sezon ${record.season_number}?`}
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Tak"
            cancelText="Nie"
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    });
  }

  const sortedSeasons = [...seasons].sort(
    (a, b) => a.season_number - b.season_number,
  );

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            Sezony ({seasons.length})
          </Title>
          {!readOnly && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
              size="small"
            >
              Dodaj sezon
            </Button>
          )}
        </div>
      }
      style={{ marginTop: 24 }}
    >
      {seasons.length === 0 ? (
        <Empty description="Brak sezonów" image={Empty.PRESENTED_IMAGE_SIMPLE}>
          {!readOnly && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
            >
              Dodaj pierwszy sezon
            </Button>
          )}
        </Empty>
      ) : (
        <Table
          dataSource={sortedSeasons}
          columns={columns}
          rowKey="id"
          pagination={false}
          size="small"
        />
      )}

      <Modal
        title={
          editingSeason
            ? `Edytuj sezon ${editingSeason.season_number}`
            : "Dodaj nowy sezon"
        }
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="season_number"
            label="Numer sezonu"
            rules={[{ required: true, message: "Numer sezonu jest wymagany" }]}
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="year" label="Rok emisji">
            <InputNumber
              min={1900}
              max={2100}
              style={{ width: "100%" }}
              placeholder="np. 2024"
            />
          </Form.Item>

          <Form.Item name="episodes_count" label="Liczba odcinków">
            <InputNumber
              min={1}
              style={{ width: "100%" }}
              onChange={handleEpisodesChange}
            />
          </Form.Item>

          <Form.Item
            name="episode_duration_minutes"
            label="Średni czas odcinka (minuty)"
          >
            <InputNumber
              min={1}
              style={{ width: "100%" }}
              onChange={handleEpisodesChange}
            />
          </Form.Item>

          <Form.Item
            name="total_duration_minutes"
            label="Łączny czas sezonu (minuty)"
          >
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {editingSeason ? "Zapisz zmiany" : "Dodaj sezon"}
              </Button>
              <Button onClick={handleCloseModal}>Anuluj</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

export default SeasonsManager;
